import { DealerUserRole, SupportReason } from "@/gql/graphql";

export const SUPPORT_MODE_DEALER_ROLES = [
  DealerUserRole.DealerOwner,
  DealerUserRole.DealerSecondaryOwner,
  DealerUserRole.DealerTechnician,
] as const;

export const SUPPORT_MODE_REASONS = [
  SupportReason.AccessIssue,
  SupportReason.ContractStatusIssue,
  SupportReason.CustomerIssue,
  SupportReason.DeviceIssue,
  SupportReason.ErrorMessage,
  SupportReason.HowToGuidance,
  SupportReason.InvoicingIssue,
  SupportReason.Other,
] as const satisfies readonly SupportReason[];

export const SALES_ORDER_ADMIN_REASONS = [
  SupportReason.All,
  SupportReason.CreateContract,
  SupportReason.CreateAmendment,
  SupportReason.ReviewContract,
  SupportReason.ReviewAmendment,
  SupportReason.ViewContractStatus,
  SupportReason.ViewAmendmentStatus,
  SupportReason.Other,
] as const satisfies readonly SupportReason[];

type SupportModeReason = (typeof SUPPORT_MODE_REASONS)[number];
type SalesOrderAdminReason = (typeof SALES_ORDER_ADMIN_REASONS)[number];
type SupportReasonI18nKey =
  | (typeof SUPPORT_REASON_I18N_KEYS)[SupportModeReason]
  | (typeof SALES_ORDER_REASON_I18N_KEYS)[SalesOrderAdminReason];

export const DEALER_ROLE_I18N_KEYS = {
  [DealerUserRole.DealerOwner]: "dealer_members.roles.dealer_owner",
  [DealerUserRole.DealerSecondaryOwner]:
    "dealer_members.roles.dealer_secondary_owner",
  [DealerUserRole.DealerTechnician]: "dealer_members.roles.dealer_technician",
} as const satisfies Record<(typeof SUPPORT_MODE_DEALER_ROLES)[number], string>;

export const SUPPORT_REASON_I18N_KEYS = {
  [SupportReason.AccessIssue]: "super_admin.support_mode.reasons.access_issue",
  [SupportReason.ContractStatusIssue]:
    "super_admin.support_mode.reasons.contract_status_issue",
  [SupportReason.CustomerIssue]:
    "super_admin.support_mode.reasons.customer_issue",
  [SupportReason.DeviceIssue]: "super_admin.support_mode.reasons.device_issue",
  [SupportReason.ErrorMessage]:
    "super_admin.support_mode.reasons.error_message",
  [SupportReason.HowToGuidance]:
    "super_admin.support_mode.reasons.how_to_guidance",
  [SupportReason.InvoicingIssue]:
    "super_admin.support_mode.reasons.invoicing_issue",
  [SupportReason.Other]: "super_admin.support_mode.reasons.other",
} as const satisfies Record<SupportModeReason, string>;

export const SALES_ORDER_REASON_I18N_KEYS = {
  [SupportReason.All]: "super_admin.support_mode.sales_order_reasons.all",
  [SupportReason.CreateContract]:
    "super_admin.support_mode.sales_order_reasons.create_contract",
  [SupportReason.CreateAmendment]:
    "super_admin.support_mode.sales_order_reasons.create_amendment",
  [SupportReason.ReviewContract]:
    "super_admin.support_mode.sales_order_reasons.review_contract",
  [SupportReason.ReviewAmendment]:
    "super_admin.support_mode.sales_order_reasons.review_amendment",
  [SupportReason.ViewContractStatus]:
    "super_admin.support_mode.sales_order_reasons.view_contract_status",
  [SupportReason.ViewAmendmentStatus]:
    "super_admin.support_mode.sales_order_reasons.view_amendment_status",
  [SupportReason.Other]: "super_admin.support_mode.sales_order_reasons.other",
} as const satisfies Record<SalesOrderAdminReason, string>;

const hasReasonKey = <T extends Record<string, string>>(
  map: T,
  reason: string
): reason is Extract<keyof T, string> => reason in map;

export const supportReasonI18nKey = (
  reason: SupportReason,
  salesOrderAdminMode = false
): SupportReasonI18nKey => {
  if (salesOrderAdminMode) {
    return hasReasonKey(SALES_ORDER_REASON_I18N_KEYS, reason)
      ? SALES_ORDER_REASON_I18N_KEYS[reason]
      : SUPPORT_REASON_I18N_KEYS[SupportReason.Other];
  }

  return hasReasonKey(SUPPORT_REASON_I18N_KEYS, reason)
    ? SUPPORT_REASON_I18N_KEYS[reason]
    : SUPPORT_REASON_I18N_KEYS[SupportReason.Other];
};

export const getSupportModeReasons = (salesOrderAdminMode: boolean) =>
  salesOrderAdminMode ? SALES_ORDER_ADMIN_REASONS : SUPPORT_MODE_REASONS;

export const isSupportModeDealerRole = (
  value: string
): value is (typeof SUPPORT_MODE_DEALER_ROLES)[number] =>
  (SUPPORT_MODE_DEALER_ROLES as readonly string[]).includes(value);

export const OTHER_REASON_MAX_LENGTH = 160;
export const ADDITIONAL_NOTES_MAX_LENGTH = 250;

export const DEALER_SEARCH_MIN_LENGTH = 5;
